import { create } from 'zustand';
import { fetchEasykitPlanning, reorderEasykitTasks, setEasykitDayStatus, createEasykitTask, updateEasykitTask, moveEasykitTask, toggleEasykitTag, setEasykitTaskOnHold, unscheduleEasykitTask, deleteEasykitTask, type EasykitSoSummary, type EasykitOverlayTask } from '@/api/easykit';
import { getWeekDates, formatDateKey } from '@/utils/planning';
import { taskBelongsToCell } from '@/utils/easykit';
import type { OdooTask } from '@/types/odoo';

interface MoveMode {
  task: OdooTask;
  fromDate: string;
  fromDriverId: number;
}

interface CreateMode {
  type: 'feestdag' | 'verlof' | 'vrije_taak';
  name?: string;
}

interface ScheduleMode {
  taskId: number;
  taskName: string;
  projectName?: string;
  orderNumber?: string;
  address?: string;
}

interface ActivityLogEntry {
  time: Date;
  action: string;
  detail?: string;
}

const BASE_EASYKIT_DRIVER_IDS = [37732, 37735, 37738];

interface EasykitPlanningState {
  tasks: OdooTask[];
  overlayTasks: EasykitOverlayTask[];
  showOverlayTasks: boolean;
  setShowOverlayTasks: (value: boolean) => void;
  selectedOverlayId: number | null;
  selectOverlay: (id: number | null) => void;
  driverIds: number[];
  driverMap: Map<number, string>;
  projectNameMap: Map<number, string>;
  addressMap: Map<number, string>;
  soSummaryMap: Map<number, EasykitSoSummary>;
  dayMarkers: Map<string, Map<number, string>>; // dateKey -> driverId -> status
  holidays: Record<string, string>; // dateKey -> company-day label (main planning)
  loading: boolean;
  error: string | null;
  savedToast: string | null;
  clearSavedToast: () => void;
  /** True between drag-start and drag-end. Used to gate silentRefresh so an
   *  in-flight planning fetch can't land mid-drag and shuffle store.tasks
   *  beneath the grid. */
  dragInProgress: boolean;
  setDragInProgress: (value: boolean) => void;
  /** True from drop-commit through silentRefresh completion. Cards are disabled
   *  for drag while this is true, preventing a fast second drop from racing
   *  the optimistic state of the previous save. */
  saveInFlight: boolean;
  lastRefreshed: Date | null;
  weekOffset: number;
  selectedTaskId: number | null;
  moveMode: MoveMode | null;
  createMode: CreateMode | null;
  scheduleMode: ScheduleMode | null;
  activityLog: ActivityLogEntry[];

  fetchPlanning: (weekOffset?: number) => Promise<void>;
  nextWeek: () => void;
  prevWeek: () => void;
  goToCurrentWeek: () => void;
  selectTask: (id: number | null) => void;
  reorderTasks: (taskIds: number[]) => Promise<void>;
  setDayStatus: (date: string, driverId: number, newStatus: string) => Promise<void>;
  createTask: (data: { type: string; date: string; driverId: number; name?: string; description?: string }) => Promise<void>;
  updateTask: (taskId: number, fields: { description?: string; name?: string; prefix?: string }) => Promise<void>;
  enterMoveMode: (task: OdooTask, fromDate: string, fromDriverId: number) => void;
  exitMoveMode: () => void;
  executeMoveTask: (toDate: string, toDriverId: number) => Promise<void>;
  moveTaskToCell: (args: { taskId: number; toDate: string; toDriverId: number; insertBeforeTaskId: number | null; orderedIds?: number[] }) => Promise<void>;
  enterCreateMode: (type: 'feestdag' | 'verlof' | 'vrije_taak', name?: string) => void;
  exitCreateMode: () => void;
  executeCreateTask: (date: string, driverId: number) => Promise<void>;
  enterScheduleMode: (taskId: number, taskName: string, projectName?: string, orderNumber?: string, address?: string) => void;
  exitScheduleMode: () => void;
  executeScheduleTask: (date: string, driverId: number) => Promise<void>;
  silentRefresh: () => Promise<void>;
  toggleTag: (taskId: number, tagId: number, action: 'add' | 'remove') => Promise<void>;
  setOnHold: (taskId: number, onHold: boolean) => Promise<void>;
  unscheduleTask: (taskId: number) => Promise<void>;
  deleteTask: (taskId: number) => Promise<void>;
}

const MAX_LOG_ENTRIES = 50;
function addLocalLog(set: (fn: (s: EasykitPlanningState) => Partial<EasykitPlanningState>) => void, action: string, detail?: string) {
  // Local store (instant UI)
  set((s) => ({
    activityLog: [{ time: new Date(), action, detail }, ...s.activityLog].slice(0, MAX_LOG_ENTRIES),
  }));
}

export const useEasykitPlanningStore = create<EasykitPlanningState>((set, get) => ({
  tasks: [],
  overlayTasks: [],
  showOverlayTasks: true,
  setShowOverlayTasks: (value) => set({ showOverlayTasks: value }),
  selectedOverlayId: null,
  selectOverlay: (id) => set({ selectedOverlayId: id, selectedTaskId: null }),
  driverIds: [...BASE_EASYKIT_DRIVER_IDS],
  driverMap: new Map(),
  projectNameMap: new Map(),
  addressMap: new Map(),
  soSummaryMap: new Map(),
  dayMarkers: new Map(),
  holidays: {},
  loading: false,
  error: null,
  savedToast: null,
  clearSavedToast: () => set({ savedToast: null }),
  dragInProgress: false,
  setDragInProgress: (value) => set({ dragInProgress: value }),
  saveInFlight: false,
  lastRefreshed: null,
  weekOffset: 0,
  selectedTaskId: null,
  moveMode: null,
  createMode: null,
  scheduleMode: null,
  activityLog: [],

  fetchPlanning: async (weekOffset?: number) => {
    const offset = weekOffset ?? get().weekOffset;
    // Reset overlayTasks on fetch start so stale week's overlay doesn't bleed through
    // while loading. On fetch error we intentionally leave tasks + overlayTasks as-is
    // (existing behavior — preserves last-good view under the error toast).
    set({ loading: true, error: null, overlayTasks: [] });
    try {
      const weekInfo = getWeekDates(offset);
      const from = formatDateKey(weekInfo.days[0]);
      const to = formatDateKey(weekInfo.days[weekInfo.days.length - 1]);

      const result = await fetchEasykitPlanning(from, to);

      const driverMap = new Map<number, string>();
      for (const [id, name] of Object.entries(result.driverMap)) {
        driverMap.set(Number(id), name);
      }

      const projectNameMap = new Map<number, string>();
      for (const [id, name] of Object.entries(result.projectNameMap)) {
        projectNameMap.set(Number(id), name);
      }

      const addressMap = new Map<number, string>();
      for (const [id, addr] of Object.entries(result.addressMap)) {
        addressMap.set(Number(id), addr);
      }

      const soSummaryMap = new Map<number, EasykitSoSummary>();
      for (const [id, summary] of Object.entries(result.soSummaryMap ?? {})) {
        soSummaryMap.set(Number(id), summary);
      }

      // Convert dayMarkers from nested Record to nested Map
      const dayMarkers = new Map<string, Map<number, string>>();
      for (const [dateKey, driverStatuses] of Object.entries(result.dayMarkers)) {
        const driverStatusMap = new Map<number, string>();
        for (const [driverId, status] of Object.entries(driverStatuses)) {
          driverStatusMap.set(Number(driverId), status);
        }
        dayMarkers.set(dateKey, driverStatusMap);
      }

      set({
        tasks: result.tasks,
        overlayTasks: result.overlayTasks ?? [],
        driverIds: result.driverIds?.length ? result.driverIds : [...BASE_EASYKIT_DRIVER_IDS],
        driverMap,
        projectNameMap,
        addressMap,
        soSummaryMap,
        dayMarkers,
        holidays: result.holidays ?? {},
        loading: false,
        lastRefreshed: new Date(),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'EasyKit planning ophalen mislukt';
      set({ error: message, loading: false });
    }
  },

  nextWeek: () => {
    const next = get().weekOffset + 1;
    set({ weekOffset: next });
    get().fetchPlanning(next);
  },

  prevWeek: () => {
    const prev = get().weekOffset - 1;
    set({ weekOffset: prev });
    get().fetchPlanning(prev);
  },

  goToCurrentWeek: () => {
    set({ weekOffset: 0 });
    get().fetchPlanning(0);
  },

  silentRefresh: async () => {
    // Skip if user is in the middle of an action (would disrupt their flow).
    // Also skip during an active drag — landing a fetch mid-drag would shuffle
    // store.tasks beneath the grid and corrupt the live drag layout.
    const { moveMode, createMode, scheduleMode, loading, dragInProgress } = get();
    if (moveMode || createMode || scheduleMode || loading || dragInProgress) return;

    try {
      const offset = get().weekOffset;
      const weekInfo = getWeekDates(offset);
      const from = formatDateKey(weekInfo.days[0]);
      const to = formatDateKey(weekInfo.days[weekInfo.days.length - 1]);

      const result = await fetchEasykitPlanning(from, to);

      // Don't update if user entered a mode OR started a new drag while we were
      // fetching — applying tasks would shuffle store.tasks under their feet.
      const current = get();
      if (current.moveMode || current.createMode || current.scheduleMode || current.dragInProgress) return;

      const driverMap = new Map<number, string>();
      for (const [id, name] of Object.entries(result.driverMap)) {
        driverMap.set(Number(id), name);
      }
      const projectNameMap = new Map<number, string>();
      for (const [id, name] of Object.entries(result.projectNameMap)) {
        projectNameMap.set(Number(id), name);
      }
      const addressMap = new Map<number, string>();
      for (const [id, addr] of Object.entries(result.addressMap)) {
        addressMap.set(Number(id), addr);
      }
      const soSummaryMap = new Map<number, EasykitSoSummary>();
      for (const [id, summary] of Object.entries(result.soSummaryMap ?? {})) {
        soSummaryMap.set(Number(id), summary);
      }
      const dayMarkers = new Map<string, Map<number, string>>();
      for (const [dateKey, driverStatuses] of Object.entries(result.dayMarkers)) {
        const driverStatusMap = new Map<number, string>();
        for (const [driverId, status] of Object.entries(driverStatuses)) {
          driverStatusMap.set(Number(driverId), status);
        }
        dayMarkers.set(dateKey, driverStatusMap);
      }

      // Silent update — no loading flag, no error clearing
      set({
        tasks: result.tasks,
        overlayTasks: result.overlayTasks ?? [],
        driverIds: result.driverIds?.length ? result.driverIds : [...BASE_EASYKIT_DRIVER_IDS],
        driverMap,
        projectNameMap,
        addressMap,
        soSummaryMap,
        dayMarkers,
        holidays: result.holidays ?? {},
        lastRefreshed: new Date(),
      });
    } catch {
      // Silently ignore — don't show error for background refresh
    }
  },

  selectTask: (id) => set({ selectedTaskId: id, selectedOverlayId: null }),

  reorderTasks: async (taskIds) => {
    // Optimistic update: reorder tasks in store immediately
    const prevTasks = get().tasks;
    const taskMap = new Map(prevTasks.map((t) => [t.id, t]));
    const reorderedIds = new Set(taskIds);
    const reorderedTasks = taskIds.map((id, i) => {
      const t = taskMap.get(id);
      return t ? { ...t, sequence: i + 1 } : null;
    }).filter(Boolean) as OdooTask[];
    const otherTasks = prevTasks.filter((t) => !reorderedIds.has(t.id));
    set({ tasks: [...otherTasks, ...reorderedTasks] });

    set({ saveInFlight: true });
    try {
      await reorderEasykitTasks(taskIds);
      // Silent refresh — no loading spinner, no grid flash mid-drag-success.
      await get().silentRefresh();
      set({ savedToast: 'Opgeslagen' });
    } catch (err) {
      // Revert on error
      set({ tasks: prevTasks });
      const message = err instanceof Error ? err.message : 'Herordenen mislukt';
      set({ error: message });
    } finally {
      set({ saveInFlight: false });
    }
  },

  setDayStatus: async (date, driverId, newStatus) => {
    try {
      await setEasykitDayStatus(date, driverId, newStatus);
      const statusLabel = newStatus === 'none' ? 'Beschikbaar' : newStatus === 'LEV' ? 'Leveringen vol' : newStatus === '.' ? 'Vol' : newStatus;
      addLocalLog(set, 'Dagstatus gewijzigd', `${date} → ${statusLabel}`);
      await get().fetchPlanning();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Status wijzigen mislukt';
      set({ error: message });
    }
  },

  createTask: async (data) => {
    try {
      await createEasykitTask(data);
      await get().fetchPlanning();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Taak aanmaken mislukt';
      set({ error: message });
    }
  },

  updateTask: async (taskId, fields) => {
    try {
      const taskName = get().tasks.find(t => t.id === taskId)?.name ?? `Taak ${taskId}`;
      await updateEasykitTask(taskId, fields);
      if (fields.name) addLocalLog(set, 'Naam gewijzigd', `${taskName} → ${fields.name}`);
      if (fields.description) addLocalLog(set, 'Omschrijving gewijzigd', taskName);
      if (fields.prefix !== undefined) {
        // Trim to match what the backend actually stores (avoids log/UI drift).
        addLocalLog(set, 'Prefix gewijzigd', `${taskName} → ${JSON.stringify(fields.prefix.trim())}`);
      }
      await get().fetchPlanning();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Taak bijwerken mislukt';
      set({ error: message });
      // Re-throw so callers (e.g. EasykitTaskDetailPanel) can keep the editor
      // open and let the user retry instead of silently closing on failure.
      throw err;
    }
  },

  enterMoveMode: (task, fromDate, fromDriverId) => {
    set({ moveMode: { task, fromDate, fromDriverId }, createMode: null, scheduleMode: null });
  },

  exitMoveMode: () => {
    set({ moveMode: null });
  },

  executeMoveTask: async (toDate, toDriverId) => {
    const { moveMode } = get();
    if (!moveMode) return;

    const movedTaskId = moveMode.task.id;
    const movedTaskName = moveMode.task.name;

    // Clear immediately to prevent double-clicks
    set({ moveMode: null });

    try {
      await moveEasykitTask(movedTaskId, toDate, toDriverId);
      addLocalLog(set, 'Verplaatst', `${movedTaskName} → ${toDate}`);
      await get().fetchPlanning();
      set({ selectedTaskId: movedTaskId });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verplaatsen mislukt';
      set({ error: message });
    }
  },

  // Cross-cell drag-and-drop. Same-cell reorder is handled by the grid's drag handler
  // calling reorderTasks() directly — this action only fires when source ≠ destination.
  // driverId 0 = Afhalingen (clears installateur). Split try/catches: move failure
  // reverts, reorder failure refetches (the move succeeded server-side already).
  moveTaskToCell: async ({ taskId, toDate, toDriverId, insertBeforeTaskId, orderedIds: providedOrder }) => {
    const snapshot = get().tasks;
    const movedTask = snapshot.find((t) => t.id === taskId);
    if (!movedTask) return;

    const nextDateTime = `${toDate} 16:00:00`;
    const nextInstallateurs = toDriverId === 0 ? [] : [toDriverId];

    // Optimistic update — immutable map. Critically, we also sequence the
    // destination cell's tasks to match the final reorder. Without this, after
    // the drag layout clears the grid would briefly render by stale local
    // sequences (placing the moved card at its OLD position) until silentRefresh
    // syncs, causing a visible jump.
    // Tighter check: an empty array is a bug signal, not "not provided". The drag
    // layout always includes the moved task; if it ever sent [], something upstream
    // is broken and we shouldn't silently fall back.
    const seqMap = new Map<number, number>();
    if (providedOrder !== undefined && providedOrder.length > 0) {
      providedOrder.forEach((id, i) => seqMap.set(id, i + 1));
    }
    set((s) => ({
      tasks: s.tasks.map((t) => {
        if (t.id === taskId) {
          return {
            ...t,
            date_deadline: nextDateTime,
            x_studio_installateur: nextInstallateurs,
            sequence: seqMap.get(taskId) ?? t.sequence,
          };
        }
        const seq = seqMap.get(t.id);
        return seq !== undefined ? { ...t, sequence: seq } : t;
      }),
    }));

    // Destination order: prefer the caller-provided list (the drag layout is the
    // authoritative final order). Fall back to recomputing from store state for
    // non-drag callers (e.g. programmatic moves) that only pass insertBeforeTaskId.
    let orderedIds: number[];
    if (providedOrder && providedOrder.length > 0) {
      orderedIds = providedOrder;
    } else {
      const others = get().tasks
        .filter((t) => taskBelongsToCell(t, toDriverId, toDate))
        .filter((t) => t.id !== taskId)
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || a.id - b.id);
      if (insertBeforeTaskId === null || !others.some((t) => t.id === insertBeforeTaskId)) {
        orderedIds = [...others.map((t) => t.id), taskId];
      } else {
        const idx = others.findIndex((t) => t.id === insertBeforeTaskId);
        orderedIds = [...others.slice(0, idx).map((t) => t.id), taskId, ...others.slice(idx).map((t) => t.id)];
      }
    }

    const movedTaskName = movedTask.name;
    set({ saveInFlight: true });
    try {
      try {
        await moveEasykitTask(taskId, toDate, toDriverId);
      } catch (err) {
        set({ tasks: snapshot, error: err instanceof Error ? err.message : 'Verplaatsen mislukt' });
        return;
      }

      try {
        await reorderEasykitTasks(orderedIds);
      } catch (err) {
        // Move succeeded server-side already — refetch to truth instead of reverting.
        const message = err instanceof Error ? err.message : 'Volgorde bijwerken mislukt';
        await get().silentRefresh();
        set({ error: message });
        return;
      }

      addLocalLog(
        set,
        toDriverId === 0 ? 'Naar Afhalingen' : 'Verplaatst',
        `${movedTaskName} → ${toDate}`,
      );
      // Silent refresh — no loading spinner, no grid flash. Keeps local store sequences
      // in sync with the server reorder so the moved task lands at the right intra-cell index.
      await get().silentRefresh();
      set({ savedToast: toDriverId === 0 ? 'Naar Afhalingen' : 'Opgeslagen' });
    } finally {
      set({ saveInFlight: false });
    }
  },

  enterCreateMode: (type, name) => {
    set({ createMode: { type, name }, moveMode: null, selectedTaskId: null, scheduleMode: null });
  },

  exitCreateMode: () => {
    set({ createMode: null });
  },

  executeCreateTask: async (date, driverId) => {
    const { createMode } = get();
    if (!createMode) return;

    // Clear immediately to prevent double-clicks
    set({ createMode: null });

    try {
      if (createMode.type === 'feestdag') {
        const results = await Promise.allSettled(
          BASE_EASYKIT_DRIVER_IDS.map((id) =>
            createEasykitTask({ type: 'feestdag', date, driverId: id })
          )
        );
        const failures = results.filter((r) => r.status === 'rejected');
        if (failures.length > 0) {
          set({ error: `Feestdag aanmaken gedeeltelijk mislukt (${failures.length}/3)` });
        }
        addLocalLog(set, 'Feestdag aangemaakt', date);
      } else if (createMode.type === 'verlof') {
        await createEasykitTask({ type: 'verlof', date, driverId });
        addLocalLog(set, 'Verlof aangemaakt', date);
      } else {
        await createEasykitTask({ type: 'vrije_taak', date, driverId, name: createMode.name });
        addLocalLog(set, 'Vrije taak aangemaakt', `${createMode.name} — ${date}`);
      }
      await get().fetchPlanning();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Taak aanmaken mislukt';
      set({ error: message });
    }
  },

  enterScheduleMode: (taskId, taskName, projectName?, orderNumber?, address?) => {
    set({ scheduleMode: { taskId, taskName, projectName, orderNumber, address }, moveMode: null, createMode: null, selectedTaskId: null });
  },

  exitScheduleMode: () => {
    set({ scheduleMode: null });
  },

  executeScheduleTask: async (date, driverId) => {
    const { scheduleMode } = get();
    if (!scheduleMode) return;

    const taskId = scheduleMode.taskId;
    // Clear immediately to prevent double-clicks
    set({ scheduleMode: null });

    try {
      await moveEasykitTask(taskId, date, driverId);
      addLocalLog(set, 'Ingepland', `${scheduleMode.taskName} → ${date}`);
      await get().fetchPlanning();
      set({ selectedTaskId: taskId });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Inplannen mislukt';
      set({ error: message });
    }
  },

  toggleTag: async (taskId, tagId, action) => {
    try {
      await toggleEasykitTag(taskId, tagId, action);
      const tagName = tagId === 7 ? 'Voormiddag' : tagId === 6 ? 'Namiddag' : tagId === 12 ? 'Afhaling' : `Tag ${tagId}`;
      addLocalLog(set, `Tag ${action === 'add' ? 'toegevoegd' : 'verwijderd'}`, tagName);
      const { weekOffset } = get();
      get().fetchPlanning(weekOffset);
    } catch {
      set({ error: 'Tag wijzigen mislukt' });
    }
  },

  setOnHold: async (taskId, onHold) => {
    const taskName = get().tasks.find(t => t.id === taskId)?.name ?? `Taak ${taskId}`;
    try {
      await setEasykitTaskOnHold(taskId, onHold);
      addLocalLog(set, onHold ? 'In wacht gezet' : 'Uit wacht gehaald', taskName);
      const { weekOffset } = get();
      get().fetchPlanning(weekOffset);
    } catch {
      set({ error: 'Wachtstatus wijzigen mislukt' });
    }
  },

  unscheduleTask: async (taskId) => {
    const taskName = get().tasks.find(t => t.id === taskId)?.name ?? `Taak ${taskId}`;
    try {
      await unscheduleEasykitTask(taskId);
      addLocalLog(set, 'Uit planning verwijderd', taskName);
      set({ selectedTaskId: null });
      const { weekOffset } = get();
      get().fetchPlanning(weekOffset);
    } catch {
      set({ error: 'Taak verwijderen uit planning mislukt' });
    }
  },

  deleteTask: async (taskId) => {
    const taskName = get().tasks.find(t => t.id === taskId)?.name ?? `Taak ${taskId}`;
    try {
      await deleteEasykitTask(taskId);
      addLocalLog(set, 'Taak verwijderd', taskName);
      set({ selectedTaskId: null });
      const { weekOffset } = get();
      get().fetchPlanning(weekOffset);
    } catch {
      set({ error: 'Taak verwijderen mislukt' });
    }
  },
}));
